import {
  type HouseholdPortfolioChangeReason,
  HouseholdPortfolioChangeReasons,
} from '@ovComponents/4-module/widgets/portfolio/householdPortfolioChange';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type HouseholdPortfolioChangeEvent = {
  changeId: number;
  reason: HouseholdPortfolioChangeReason;
  expectedFinancialProductId: string | null;
};

export type HouseholdPortfolioCoordination = {
  changeEvent: HouseholdPortfolioChangeEvent;
  lastHandledChangeId: number;
  notifyChange: (
    reason: HouseholdPortfolioChangeReason,
    expectedFinancialProductId?: string | null,
  ) => void;
  ackChange: (changeId: number) => void;
};

const initialChangeEvent: HouseholdPortfolioChangeEvent = {
  changeId: 0,
  reason: HouseholdPortfolioChangeReasons.attach,
  expectedFinancialProductId: null,
};

const HouseholdPortfolioCoordinationContext =
  createContext<HouseholdPortfolioCoordination>({
    changeEvent: initialChangeEvent,
    lastHandledChangeId: 0,
    notifyChange: () => {},
    ackChange: () => {},
  });

export const HouseholdPortfolioCoordinationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [changeEvent, setChangeEvent] =
    useState<HouseholdPortfolioChangeEvent>(initialChangeEvent);
  const [lastHandledChangeId, setLastHandledChangeId] = useState(0);

  const notifyChange = useCallback(
    (
      reason: HouseholdPortfolioChangeReason,
      expectedFinancialProductId?: string | null,
    ) => {
      setChangeEvent((prev) => {
        const changeId = prev.changeId + 1;
        let nextExpectedFinancialProductId = prev.expectedFinancialProductId;

        if (reason === HouseholdPortfolioChangeReasons.detach) {
          nextExpectedFinancialProductId = null;
        } else if (reason === HouseholdPortfolioChangeReasons.attach) {
          nextExpectedFinancialProductId = expectedFinancialProductId ?? null;
        }

        if (reason === HouseholdPortfolioChangeReasons.detach) {
          setLastHandledChangeId(changeId);
        }

        return {
          changeId,
          reason,
          expectedFinancialProductId: nextExpectedFinancialProductId,
        };
      });
    },
    [],
  );

  const ackChange = useCallback((changeId: number) => {
    setLastHandledChangeId((prev) => Math.max(prev, changeId));
    setChangeEvent((prev) => {
      if (
        prev.reason === HouseholdPortfolioChangeReasons.detach &&
        changeId >= prev.changeId
      ) {
        return {
          changeId: prev.changeId,
          reason: HouseholdPortfolioChangeReasons.attach,
          expectedFinancialProductId: null,
        };
      }

      return prev;
    });
  }, []);

  const value = useMemo(
    () => ({
      changeEvent,
      lastHandledChangeId,
      notifyChange,
      ackChange,
    }),
    [ackChange, changeEvent, lastHandledChangeId, notifyChange],
  );

  return (
    <HouseholdPortfolioCoordinationContext.Provider value={value}>
      {children}
    </HouseholdPortfolioCoordinationContext.Provider>
  );
};

export const useHouseholdPortfolioCoordination = () =>
  useContext(HouseholdPortfolioCoordinationContext);
